import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DoneIcon from "@mui/icons-material/Done";
import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Typography
} from "@mui/material";
import { difference, isEqual, uniq } from "lodash";

import { calcFieldsToDisplay, DEAL_INDEX, indexNames } from "../../../constants/reports";
import { useAccountSelector, useReportsActions, useReportsQueriesSelector, useUserActions } from "../../../store";
import { GroupBy, ReportsQueries, ReportTableColumn, ReportTableColumnBy, RowBy } from "../../../store/types";
import syncColumns from "../../../utils/sync-report-table-column";
import { SelectOption } from "../AutocompleteMultiSelect/AutocompleteMultiSelect";
import { OpportunityBlock } from "./Opportunity/OpportunityBlock";
import { ReportGroupByMenu } from "./ReportGroupByMenu";
import { ReportSetTableColumns } from "./ReportSetTableColumns";
import { ReportSetTableGroupBy } from "./ReportSetTableGroupBy";

const rowByOptions: SelectOption<RowBy>[] = [
  { text: "Investor", key: "Investor", value: "investor" },
  { text: "Deal", key: "Deal", value: "deal" },
  { text: "Measure", key: "Measure", value: "measure" }
];

const columnByOptions: SelectOption<ReportTableColumnBy | "date">[] = [
  { text: "Measure", key: "Measure", value: "measure" },
  { text: "Investor", key: "Investor", value: "investor" },
  { text: "Date", key: "Date", value: "date" }
];

const dateColumnOptions = [
  { text: "Date - year", value: "year" },
  { text: "Date - quarter", value: "quarter" },
  { text: "Date - month", value: "month" }
];

interface IReportTableToolbar {
  groupByOptions: SelectOption[];
  measures: string[];
  characteristics: string[];
  calculatedFields: string[];
  groupBy: GroupBy;
  rowBy: RowBy;
  originalCurrency: boolean;
  indexName?: string;
  isTableLoading: boolean;
  columnBy?: ReportTableColumnBy;
  isSnapshot?: boolean;
  secondaryPricing?: string[];
  /** Override the columns to sync against (e.g. client snapshot). Defaults to reports.queries.reportTableColumns. */
  columns?: ReportTableColumn[];
  /** How to persist synced columns. Defaults to the snapshot behaviour (updateSnapshotTableColumnsAction). */
  onColumnsChange?: (columns: ReportTableColumn[]) => void;
}

export const ReportTableToolbar: React.FC<IReportTableToolbar> = ({
  groupByOptions,
  measures,
  characteristics,
  calculatedFields,
  groupBy,
  rowBy,
  originalCurrency,
  indexName,
  isTableLoading,
  columnBy = "measure",
  isSnapshot,
  secondaryPricing,
  columns: columnsOverride,
  onColumnsChange
}) => {
  const queriesFromStore: Partial<ReportsQueries> = {
    measures,
    characteristics,
    calculatedFields,
    groupBy,
    rowBy,
    originalCurrency,
    indexName,
    columnBy,
    secondaryPricing
  };

  const [options, setOptions] = useState<Partial<ReportsQueries>>(queriesFromStore);
  const [pmeOpen, setPmeOpen] = useState(false);
  const pmeChipRef = useRef<HTMLDivElement>(null);
  const dateChipRef = useRef<HTMLDivElement>(null);
  const [dateMenuOpen, setDateMenuOpen] = useState(false);
  const [isMoreActive, setIsMoreActive] = useState(true);
  const isMoreActiveRef = useRef(isMoreActive);
  isMoreActiveRef.current = isMoreActive;

  const { setQueriesAction } = useReportsActions();
  const { updateSnapshotTableColumnsAction } = useUserActions();
  const { reportTableColumns: reportTableColumnsGlobal } = useReportsQueriesSelector();
  const reportTableColumns = columnsOverride ?? reportTableColumnsGlobal;
  const persistColumns = onColumnsChange ?? updateSnapshotTableColumnsAction;
  const { show_secondary_pricing } = useAccountSelector() || {};

  useEffect(() => {
    if (isMoreActiveRef.current) {
      setOptions(queriesFromStore);
    } else {
      // Preserve "more" draft selections when toggle is off
      setOptions((prev) => ({
        ...queriesFromStore,
        measures: prev.measures,
        characteristics: prev.characteristics,
        calculatedFields: prev.calculatedFields
      }));
    }
  }, [
    measures,
    characteristics,
    calculatedFields,
    groupBy,
    rowBy,
    originalCurrency,
    indexName,
    columnBy,
    secondaryPricing
  ]);

  // Sync parameters from reportTableColumns on mount/load
  // This ensures that if columns were persisted (e.g., MOIC), they appear as selected in the toolbar
  useEffect(() => {
    if (!reportTableColumns?.length) return;

    const aggregationsInColumns = reportTableColumns.map((col) => col.aggregation);
    const allStoreParams = [...measures, ...characteristics, ...calculatedFields, ...(secondaryPricing || [])];

    // Find parameters that exist in columns but not in current store parameters
    const missingParams = aggregationsInColumns.filter((agg) => !allStoreParams.includes(agg));

    if (missingParams.length) {
      // Add missing parameters to local options state to highlight them in the toolbar
      // Assume missing parameters are calculated fields (most common case)
      setOptions((prev) => ({
        ...prev,
        calculatedFields: [...(prev.calculatedFields || []), ...missingParams]
      }));
    }
  }, [reportTableColumns, measures, characteristics, calculatedFields, secondaryPricing]);

  const onCancel = () => {
    setOptions({
      measures,
      characteristics,
      calculatedFields,
      groupBy,
      rowBy,
      originalCurrency,
      indexName,
      columnBy,
      secondaryPricing
    });
    setIsMoreActive(true);
  };

  const selectedColumnByOption = useMemo(() => {
    const selected = columnByOptions.find((opt) => {
      if (opt.value === "date" && ["year", "quarter", "month"].includes(options.columnBy as string)) {
        return true;
      }
      return options.columnBy === opt.value;
    });

    return selected;
  }, [options.columnBy]);

  const getDateColumnText = useMemo(() => {
    const columnBy = options.columnBy as string;
    if (columnBy === "year") return "Date - year";
    if (columnBy === "quarter") return "Date - quarter";
    if (columnBy === "month") return "Date - month";
    return "Date";
  }, [options.columnBy]);

  const parameters = useMemo(
    () => [
      ...(options.measures as string[]),
      ...(options.characteristics as string[]),
      ...(options.calculatedFields as string[])
    ],
    [options]
  );

  const handleParameterClick = useCallback(
    (parameter: string) => {
      if (parameter === "PME") {
        setPmeOpen(true);
      } else {
        onToggleStaticParameter(parameter);
      }
    },
    [options]
  );

  const handleSelectIndex = useCallback((indexName: string) => {
    setPmeOpen(false);
    if (indexName === "none") {
      setOptions((prev) => ({
        ...prev,
        calculatedFields: prev.calculatedFields?.filter((item) => item !== "PME"),
        indexName: undefined
      }));
    } else if (indexName === DEAL_INDEX) {
      setOptions((prev) => ({
        ...prev,
        calculatedFields: [...(prev.calculatedFields || []), "PME"],
        indexName: undefined
      }));
    } else {
      setOptions((prev) => ({
        ...prev,
        calculatedFields: [...(prev.calculatedFields || []), "PME"],
        indexName
      }));
    }
  }, []);

  const onToggleStaticParameter = useCallback(
    (parameter: string) => {
      if (options.calculatedFields) {
        if (options.calculatedFields.includes(parameter)) {
          setOptions((prev) => ({
            ...prev,
            calculatedFields: prev.calculatedFields?.filter((item) => item !== parameter)
          }));
        } else {
          setOptions((prev) => ({
            ...prev,
            calculatedFields: [...(prev.calculatedFields || []), parameter],
            ...(parameter === "PME" ? { indexName } : {})
          }));
        }
      }
    },
    [options]
  );

  const selectedUniqueParameters = useMemo(() => {
    return difference(parameters, calcFieldsToDisplay);
  }, [parameters, calcFieldsToDisplay]);

  const effectiveOptions = useMemo(() => {
    if (isMoreActive) return options;
    const isUnique = (p: string) => selectedUniqueParameters.includes(p);
    return {
      ...options,
      measures: options.measures?.filter((p) => !isUnique(p)),
      characteristics: options.characteristics?.filter((p) => !isUnique(p)),
      calculatedFields: options.calculatedFields?.filter((p) => !isUnique(p))
    };
  }, [isMoreActive, options, selectedUniqueParameters]);

  const onSubmit = useCallback(() => {
    const updatedQueries = { ...effectiveOptions, calculatedFields: uniq(effectiveOptions.calculatedFields) };
    setQueriesAction(updatedQueries);

    // If parameters changed, sync and persist reportTableColumns
    if (
      effectiveOptions.measures ||
      effectiveOptions.calculatedFields ||
      effectiveOptions.characteristics ||
      effectiveOptions.secondaryPricing
    ) {
      const syncedColumns = syncColumns(
        {
          measures: effectiveOptions.measures,
          calculatedFields: uniq(effectiveOptions.calculatedFields),
          characteristics: effectiveOptions.characteristics,
          secondaryPricing: effectiveOptions.secondaryPricing
        },
        reportTableColumns
      );
      persistColumns(syncedColumns);
    }
  }, [effectiveOptions, reportTableColumns, setQueriesAction, persistColumns]);

  const isBtnDisabled = useMemo(() => {
    return isEqual(effectiveOptions, queriesFromStore) || isTableLoading;
  }, [isTableLoading, effectiveOptions, queriesFromStore]);

  const groupByLevels = useMemo(() => {
    const levels: SelectOption[] = [];

    // Add all groupBy levels (can be 0, 1, 2, 3+ when currency is injected)
    (options.groupBy?.flat() || []).forEach((value) => {
      const option = groupByOptions.find((opt) => opt.value === value);
      if (option) {
        levels.push(option);
      } else {
        // Handle currency or other non-standard groups
        levels.push({ text: value, key: value, value });
      }
    });

    // Add rowBy as final level
    const rowByOption = rowByOptions.find((opt) => options.rowBy === opt.value);
    if (rowByOption) {
      levels.push(rowByOption);
    }

    return levels;
  }, [groupByOptions, rowByOptions, options.groupBy, options.rowBy]);

  const handleColumnByClick = (option: SelectOption<ReportTableColumnBy | "date">) => {
    const value = option.value;
    if (value === "date") {
      setDateMenuOpen(true);
    } else {
      setOptions((prev) => ({
        ...prev,
        columnBy: value
      }));
    }
  };

  const handleDateOptionSelect = (value: string) => {
    setOptions((prev) => ({
      ...prev,
      columnBy: value as ReportTableColumnBy
    }));
    setDateMenuOpen(false);
  };

  const handleSecondaryPricingClick = () => {
    setOptions((prev) => ({
      ...prev,
      secondaryPricing: prev.secondaryPricing?.includes("Secondary Pricing") ? [] : ["Secondary Pricing"]
    }));
  };

  return (
    <Stack sx={{ width: "100%", gap: "16px" }}>
      <Stack
        sx={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "10px"
        }}
      >
        <Stack flexDirection="column" justifyContent="space-between" alignItems="flex-start" gap="10px">
          <Stack direction="row" gap="10px" alignItems="center">
            <Typography variant="body3" fontWeight="500">
              Parameters:
            </Typography>
            <Stack flexDirection="row" gap="10px" alignItems="center">
              {calcFieldsToDisplay.map((parameter) => {
                return (
                  <FormControlLabel
                    key={parameter}
                    label=""
                    onClick={() => handleParameterClick(parameter)}
                    sx={{ margin: 0 }}
                    control={
                      <Chip
                        ref={parameter === "PME" ? pmeChipRef : undefined}
                        label={parameter}
                        sx={{
                          color: "#071F35",
                          fontSize: "12px",
                          padding: "4px 8px",
                          height: "24px",
                          ...(parameters.includes(parameter) && {
                            backgroundColor: "#c3c9cf !important"
                          })
                        }}
                      />
                    }
                  />
                );
              })}
              {show_secondary_pricing && Boolean(isSnapshot) && (
                <Chip
                  onClick={handleSecondaryPricingClick}
                  label={"Secondary Pricing"}
                  sx={{
                    color: "#071F35",
                    fontSize: "12px",
                    padding: "4px 8px",
                    height: "24px",
                    ...(options.secondaryPricing?.includes("Secondary Pricing") && {
                      backgroundColor: "#c3c9cf !important"
                    })
                  }}
                />
              )}
              <Menu anchorEl={pmeChipRef.current} open={pmeOpen} onClose={() => setPmeOpen(false)}>
                <MenuItem onClick={() => handleSelectIndex("none")}>None</MenuItem>
                <MenuItem
                  selected={!options.indexName && options.calculatedFields?.includes("PME")}
                  onClick={() => handleSelectIndex(DEAL_INDEX)}
                >
                  Use deal specific index
                </MenuItem>
                {Object.entries(indexNames).map(([index, name]) => (
                  <MenuItem key={index} selected={index === options.indexName} onClick={() => handleSelectIndex(index)}>
                    {name}
                  </MenuItem>
                ))}
              </Menu>
              {Boolean(selectedUniqueParameters.length) && (
                <FormControlLabel
                  label=""
                  onClick={() => setIsMoreActive((prev) => !prev)}
                  sx={{ margin: 0 }}
                  control={
                    <Chip
                      label={
                        <Typography
                          sx={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            fontSize: "12px"
                          }}
                        >
                          ({selectedUniqueParameters.length})
                        </Typography>
                      }
                      sx={{
                        color: "#071F35",
                        height: "24px",
                        cursor: "pointer",
                        ...(isMoreActive && {
                          backgroundColor: "#c3c9cf !important"
                        })
                      }}
                    />
                  }
                />
              )}
              <ReportGroupByMenu
                renderTrigger={(params) => (
                  <>
                    {isTableLoading ? (
                      <CircularProgress size="20px" />
                    ) : (
                      <Typography
                        {...params}
                        color="#26A6AE"
                        fontSize="14px"
                        sx={{ cursor: "pointer", "&:hover": { fontWeight: 600 } }}
                      >
                        More
                      </Typography>
                    )}
                  </>
                )}
                setOptions={setOptions}
                // setOptions={(values) => setOptions((prev) => ({ ...prev, ...values }))}
              >
                <ReportSetTableColumns defaultQueries={options} />
              </ReportGroupByMenu>
            </Stack>
          </Stack>
          <Stack direction="row" gap="8px" alignItems="center">
            <Typography variant="body3" fontWeight="500" whiteSpace="nowrap">
              Row By:
            </Typography>
            <ReportGroupByMenu
              setOptions={setOptions}
              // setOptions={(values) => setOptions((prev) => ({ ...prev, ...values }))}
              renderTrigger={(params) => (
                <>
                  {isTableLoading ? (
                    <CircularProgress size="20px" />
                  ) : (
                    <Stack {...params} flexDirection="row" justifyContent="center" alignItems="center" gap="4px">
                      {groupByLevels.map((option, index) => (
                        <Fragment key={`${option.value}-${index}`}>
                          {index > 0 && <ArrowForwardIosIcon sx={{ fontSize: "14px" }} />}
                          <Chip
                            label={option.text || "None"}
                            sx={{
                              color: "#071F35",
                              fontSize: "12px",
                              padding: "4px 8px",
                              height: "24px",
                              textTransform: "capitalize",
                              cursor: "pointer"
                            }}
                          />
                        </Fragment>
                      ))}
                    </Stack>
                  )}
                </>
              )}
            >
              <ReportSetTableGroupBy defaultQueries={options} groupByOptions={groupByOptions} />
            </ReportGroupByMenu>
          </Stack>
        </Stack>
        <Stack flexDirection="row" gap="10px">
          <Stack flexDirection="column" justifyContent="space-between" alignItems="flex-start">
            {isSnapshot && (
              <Stack direction="row" gap="10px">
                <Typography
                  variant="body3"
                  fontWeight="500"
                  whiteSpace="nowrap"
                  sx={{
                    ...((options.columnBy === "year" ||
                      options.columnBy === "quarter" ||
                      options.columnBy === "month") && {
                      color: "#bac7ce"
                    })
                  }}
                >
                  Original Currency:
                </Typography>
                <Switch
                  disabled={
                    options.columnBy === "year" || options.columnBy === "quarter" || options.columnBy === "month"
                  }
                  focusVisibleClassName=".Mui-focusVisible"
                  disableRipple
                  checked={options.originalCurrency}
                  onChange={() =>
                    setOptions((prev) => {
                      return {
                        ...prev,
                        originalCurrency: !options.originalCurrency
                      };
                    })
                  }
                  sx={{
                    width: 48,
                    height: 24,
                    padding: 0,
                    "& .MuiSwitch-switchBase": {
                      padding: 0,
                      margin: "2px",
                      transitionDuration: "300ms",
                      color: "#bac7ce",
                      "&.Mui-checked": {
                        transform: "translateX(23px)",
                        color: "#0a3142",
                        "& + .MuiSwitch-track": {
                          backgroundColor: "#dae0e2",
                          opacity: 1,
                          border: 0
                        },
                        "&.Mui-disabled + .MuiSwitch-track": {
                          opacity: 0.5
                        }
                      },
                      "&.Mui-focusVisible .MuiSwitch-thumb": {
                        color: "#33cf4d",
                        border: "6px solid #fff"
                      },
                      "&.Mui-disabled .MuiSwitch-thumb": {
                        opacity: 0.7
                      },
                      "&.Mui-disabled + .MuiSwitch-track": {
                        opacity: 0.3
                      }
                    },
                    "& .MuiSwitch-thumb": {
                      boxSizing: "border-box",
                      width: 20,
                      height: 20
                    },
                    "& .MuiSwitch-track": {
                      borderRadius: "34px",
                      backgroundColor: "#dae0e2",
                      opacity: 1,
                      transition: "background-color 500ms"
                    }
                  }}
                />
              </Stack>
            )}
            {isSnapshot && (
              <Stack direction="row" gap="10px" alignItems="center">
                <Typography variant="body3" fontWeight="500" whiteSpace="nowrap">
                  Column By:
                </Typography>
                {columnByOptions.map((option) => {
                  return (
                    <FormControlLabel
                      key={option.key}
                      value={option}
                      sx={{ margin: 0 }}
                      onClick={() => handleColumnByClick(option)}
                      control={
                        <Chip
                          {...(selectedColumnByOption === option && {
                            icon: <DoneIcon sx={{ fontSize: "16px", fill: "#071F35" }} />
                          })}
                          ref={option.key === "Date" ? dateChipRef : undefined}
                          label={option.key === "Date" ? getDateColumnText : option.text}
                          sx={{
                            height: "24px",
                            ...(selectedColumnByOption === option && {
                              backgroundColor: "#c3c9cf !important"
                            })
                          }}
                        />
                      }
                      label=""
                    />
                  );
                })}
                <Menu anchorEl={dateChipRef.current} open={dateMenuOpen} onClose={() => setDateMenuOpen(false)}>
                  {dateColumnOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      selected={options.columnBy === option.value}
                      onClick={() => handleDateOptionSelect(option.value)}
                    >
                      {option.text}
                    </MenuItem>
                  ))}
                </Menu>
              </Stack>
            )}
          </Stack>
          <Divider sx={{ borderWidth: 0, height: "58px", width: "1px", backgroundColor: "#DBE8F5" }} />
          <Stack flexDirection="column" alignItems="center" justifyContent="flex-end" gap="10px">
            <Button
              size="sm"
              variant="outlined"
              sx={{
                minWidth: "55px",
                "&:disabled": {
                  backgroundColor: "white",
                  color: "#bac7ce",
                  borderColor: "#bac7ce"
                }
              }}
              onClick={onCancel}
              disabled={isBtnDisabled}
            >
              Cancel
            </Button>
            <Button
              onClick={onSubmit}
              size="sm"
              variant="contained"
              sx={{ backgroundColor: "#071F35", minWidth: "55px" }}
              disabled={isBtnDisabled}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <OpportunityBlock isTableLoading={isTableLoading} />
    </Stack>
  );
};
